import React, { useState, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import { Fx, Icon, Stack } from 'Components';
import { useStores } from 'Hooks/useStore';
import { DashboardContext, ToastType } from 'Pages/Dashboard/DashboardContext';
import DashboardFilters from 'Pages/Dashboard/DashboardFilters/DashboardFilters';
import { DashboardFiltersValues } from 'Pages/Dashboard/Dashboard.types';
import { APP_ROUTES, getAppRoutes } from 'Pages/App/App.constants';
import HeaderRow from './components/HeaderRow';
import { useTranslation } from 'react-i18next';
import Toaster from 'PhishingManager/components/Toast';
import { navigateBack } from 'Helpers/navigation';
import { ALWAYS_ENBALED_ROUTES, getRawPath } from 'Components/Sidebar/Siderbar.constant';
import EmptyState from 'PhishingManager/components/EmptyState';
import PropertySelection from 'Components/Sidebar/components/PropertySelection';

const toastInitialValue: ToastType = {
	message: '',
	type: 'warning',
	icon: 'check'
};
function TenantProvider({
	children,
	showHeader
}: {
	children: React.ReactNode;
	showHeader: boolean;
}) {
	const stores = useStores();
	const { authStore, systemsStore } = stores;
    const { tenantLoading, selectedTenant } = systemsStore;
    const isSuperAdmin = authStore.isCurrentUserSuperAdmin;
    const [filterValues, setFilterValues] = useState<DashboardFiltersValues>({});
    const [userFilterValues, setUserFilterValues] = useState<DashboardFiltersValues>({});
    const [toast, setToast] = useState<ToastType>({ ...toastInitialValue });
	const [hasProperties, setHasProperties] = useState(true);

	const { pathname, search } = useLocation();
	const history = useHistory();
	const { t } = useTranslation();
	const appRoutes = getAppRoutes();

    const onDashboardFiltersChange = (value: DashboardFiltersValues, ) => {
        setUserFilterValues({ ...userFilterValues, ...value });
    }

    const onPropertyChange = (propertyId: number) => {
        const isProperty = propertyId === 0;
        setFilterValues({ id: isProperty ? selectedTenant.value : propertyId, isProperty })
    }

    const setToastInfo = (value: ToastType) => setToast(value);
    
    const componentDetails = useMemo(() => {
        let details = null;
        const normalizedPath = pathname === '/' ? pathname : pathname.replace(/\/+$/, '');
        if (appRoutes.USER_ACTIVITY === normalizedPath) {
            details = {
                title: t("pageHeader.userActivity.title"),
                subTitle: t("pageHeader.userActivity.description"),
                showFilter: "legacy"
            }
        } else if (appRoutes.PHISHING === normalizedPath) {
            details = {
                title: t("pageHeader.phishing.title"),
                subTitle: t("pageHeader.phishing.description"),
                showFilter: null
            }
        } else if (appRoutes.TRAINING_REPORT === normalizedPath) {
            details = {
                title: t("learning.report.title"),
                subTitle: t("learning.report.subTitle"),
                showFilter: null
            }
        } else if (appRoutes.COURSES === normalizedPath) {
            details = {
                title: t("pageHeader.courses.title"),
                subTitle: t("pageHeader.courses.description"),
                showFilter: "legacy"
            }
        } else if (appRoutes.REPORTS === normalizedPath) {
            details = {
                title: t("pageHeader.library.title"),
                subTitle: t("pageHeader.library.description"),
                showFilter: null
            }
        } else if (appRoutes.SAVED_REPORTS === normalizedPath) {
            details = {
                title: t("pageHeader.savedReports.title"),
                subTitle: t("pageHeader.savedReports.description"),
                showFilter: null
            }
        } else if (appRoutes.HOME === normalizedPath) {
            details = {
                title: t("pageHeader.dashboard.title"),
                subTitle: t("pageHeader.dashboard.description"),
                showFilter: "property"
            }
        } else if (appRoutes.PROFILE === normalizedPath) {
            details = {
                title: t("pageHeader.profile.title"),
                subTitle: t("pageHeader.profile.description"),
                showFilter: null
            }
        } else if (appRoutes.ADD_USER === normalizedPath) {
            details = {
                title: t("pageHeader.addUser.title"),
                subTitle: t("pageHeader.addUser.description"),
                showFilter: null,
                headerIcon: <Icon type="summaryBack" onClick={() => navigateBack(history, getAppRoutes().USERS)} />
            }
        } else if (appRoutes.SAQ_COMPLAINCE === normalizedPath) {
            details = {
                title: t("pciCompliance.title"),
                subTitle: t("pciCompliance.subtitle"),
                showFilter: null
            }
        } else if (appRoutes.SCANNING === normalizedPath) {
            details = {
                title: t("pciCompliance.scanTitle"),
                subTitle: t("pciCompliance.scanSubtitle"),
                showFilter: null
            }
        } else if (appRoutes.PHISHING_MANAGER === normalizedPath) {
            details = {
                title: t("phishingManager.title"),
                subTitle: t("phishingManager.description"),
                showFilter: null
            }
        } else if (appRoutes.MAPPING === normalizedPath) {
            details = {
                title: t("mapping.mappingSettings.title"),
                subTitle: t("mapping.mappingSettings.description"),
                showFilter: null
            }
        } else if (appRoutes.PHISHING_TEMPLATE_MANAGER === normalizedPath) {
            details = {
                title: t("phishingTemplateManager.title"),
                subTitle: t("phishingTemplateManager.description"),
                showFilter: null
            }
        } else if (appRoutes.ROLES === normalizedPath) {
            details = {
                title: t("roles.title"),
                subTitle: t("roles.description"),
                showFilter: null
            }
        } else if(appRoutes.FEATURE_MANAGER === normalizedPath) {
            details = {
                title: t(`pageHeader.featureManager.title`),
                subTitle: t(`pageHeader.featureManager.description`),
                showFilter: null
            }
        }
        return details;
    }, [pathname, t]);
    const legacySelector = componentDetails?.showFilter === "legacy";
    const contextFilterValue = legacySelector ? userFilterValues : filterValues;

    const childrenView = useMemo(() => {
        if(isSuperAdmin) {
            const { tenantLoading, selectedTenant } = systemsStore;
            if(selectedTenant.value === 0) {
                const rawPath = getRawPath(pathname);
                if(ALWAYS_ENBALED_ROUTES.includes(rawPath)) return children;
                else {
                    const EMPTY_VIEW = <Spin spinning={tenantLoading}><EmptyState title={t('sidebar.tenantSelector.noTenantTitle')} description={[t('sidebar.tenantSelector.noTenantDesc')]} actionLabel={null} /></Spin>
                    
                    if([APP_ROUTES.PHISHING_MANAGER, APP_ROUTES.MAPPING, APP_ROUTES.TRAINING_REPORT].includes(rawPath)) {
                        return <>
                                <HeaderRow title={componentDetails?.title || ""} subHeading={componentDetails?.subTitle || ""} />
                                {EMPTY_VIEW}
                            </>
                    }
                    return EMPTY_VIEW;
                }
            }
            return children;
        }
        return children;
    }, [pathname, search, isSuperAdmin, selectedTenant, tenantLoading])

	useEffect(() => {
		return () => stores.clearDashboardStore();
	}, []);

    return (
        <DashboardContext.Provider value={{ filters: contextFilterValue, hasProperties, setToastInfo }}>
            <Fx column grow={1} style={{ display: showHeader ? "block" : "none" }}>
                <Stack>
                    <HeaderRow title={componentDetails?.title || ""} subHeading={componentDetails?.subTitle || ""} icon={componentDetails?.headerIcon || null}>
                        <DashboardFilters {...{ filterValues: contextFilterValue, isSuperAdmin }} showFilter={legacySelector} onChange={onDashboardFiltersChange} onSetHasProperties={setHasProperties} />
                        <PropertySelection {...{ systemsStore, isSuperAdmin, onPropertyChange, showFilter: !!(componentDetails?.showFilter && !legacySelector) }} />
                    </HeaderRow>
                    {showHeader ? childrenView : null}
                </Stack>
            </Fx>
            {showHeader ? null : childrenView}
            <Toaster visible={!!toast.message} message={toast?.message ?? ''} type={toast?.type ?? 'warning'} icon={toast?.icon || "campaignInfo"} onClose={() => setToastInfo({ ...toastInitialValue })} />
        </DashboardContext.Provider>
    );
}

export default observer(TenantProvider);
